import type { Table } from "@tanstack/react-table";

import { useUIStateStore } from "@/store/uiStateStore";

import { Button } from "../button/Button";
import {
  IconTablePageEnd,
  IconTablePageNext,
  IconTablePagePrev,
  IconTablePageStart,
} from "../icon/Icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select/Select";
import Text from "../typography/Text";

type Props<TData> = {
  table: Table<TData>;
};

function TablePagination<TData>({ table }: Props<TData>) {
  const currentPage = table.getState().pagination.pageIndex + 1;
  const pageCount = table.getPageCount();
  const currentPageSize = table.getState().pagination.pageSize;
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const pageSizes = [5, 10, 20, 30, 40, 50];
  const hasValidPageCount = typeof pageCount === "number" && pageCount > 0;

  return (
    <div className="w-full justify-between flex flex-col sm:flex-row gap-0 items-center mt-2">
      <Select
        value={String(currentPageSize)}
        onValueChange={(value) => {
          // This call is now safe
          table.setPageSize(Number(value));
        }}
      >
        <SelectTrigger className="pl-0 w-auto">
          <SelectValue placeholder={`Show ${currentPageSize} rows`} />
        </SelectTrigger>
        <SelectContent>
          {pageSizes.map(pageSize => (
            <SelectItem key={pageSize} value={String(pageSize)}>
              Show
              {" "}
              {pageSize}
              {" "}
              rows
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Only show page controls if pageCount is valid */}
      {hasValidPageCount === true && (
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <div className="flex flex-row items-center gap-2 mr-2 h-full   ">
            <Text as="p" className="text-sm">
              Page
              {" "}
            </Text>
            <Select
              value={String(currentPage)}
              onValueChange={(value) => {
                // This call is now safe
                table.setPageIndex(Number(value) - 1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={String(currentPage)} />
              </SelectTrigger>
              <SelectContent>
                {pageCount > 0 && Array.from({ length: pageCount }, (_, index) => (
                  <SelectItem key={index + 1} value={String(index + 1)}>
                    {index + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Text as="p" className="text-sm">
              of
              {" "}
              {pageCount.toLocaleString()}
            </Text>
          </div>
          <div className="flex gap-2 mr-2">
            <Button
              variant={isTunecore ? "primary" : "darkSecondary"}
              className="w-auto shadow-md"
              onClick={() => table.firstPage()}
              disabled={table.getCanPreviousPage() === false}
              label={(
                <IconTablePageStart
                  className="stroke-white "
                  width="20"
                  height="20"
                  fill="none"
                />
              )}
            />
            <Button
              variant={isTunecore ? "primary" : "darkSecondary"}
              className="w-auto shadow-md"
              onClick={() => table.previousPage()}
              disabled={table.getCanPreviousPage() === false}
              label={(
                <IconTablePagePrev
                  className="stroke-white "
                  width="20"
                  height="20"
                  fill="none"
                />
              )}
            />
            <Button
              variant={isTunecore ? "primary" : "darkSecondary"}
              className="w-auto shadow-md stroke-red-500"
              onClick={() => table.nextPage()}
              // Explicit boolean check
              disabled={table.getCanNextPage() === false}
              label={(
                <IconTablePageNext
                  className="stroke-white dark:group-hover:stroke-black"
                  width="20"
                  height="20"
                  fill="none"
                />
              )}
            />
            <Button
              variant={isTunecore ? "primary" : "darkSecondary"}
              className="w-auto shadow-md"
              onClick={() => table.lastPage()}
              disabled={table.getCanNextPage() === false}
              label={(
                <IconTablePageEnd
                  className="stroke-white dark:group-hover:stroke-black"
                  width="20"
                  height="20"
                  fill="none"
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TablePagination;
